"use client";

import { AtSign, MapPin, User, Users } from "lucide-react";
import { usePathname, useSearchParams } from "next/navigation";
import { useEffect, useRef, useState } from "react";
import type { EntityStoryStatus } from "@/actions/story";
import type { CommentsSheetViewer } from "@/components/posts/comments-sheet";
import { ProfileStickersSection } from "@/components/stickers/profile-stickers-section";
import { AppImage as Image } from "@/components/ui/app-image";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { BackAwareLink } from "@/components/ui/back-aware-link";
import { BackButton } from "@/components/ui/back-button";
import { Badge } from "@/components/ui/badge";
import { useIsClampedOverflow } from "@/components/ui/clamped-text.client";
import { ProfileMedia } from "@/components/ui/profile-media";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import type {
  ActivityId,
  ActivitySkill,
  Goal,
  ImageData,
} from "@/db/schema/profile";
import { AddMusicLiquidButton } from "@/features/profile/components/profile-music/my-music-picker";
import { ProfileMusicSection } from "@/features/profile/components/profile-music/profile-music-section";
import { EntityStoryRing } from "@/features/stories/components/entity-story-ring/entity-story-ring.client";
import { StoryHighlightsRow } from "@/features/stories/components/highlights/story-highlights-row.client";
import { useRailScrollRestore } from "@/hooks/use-rail-scroll-restore";
import { useScreenView } from "@/hooks/use-screen-view";
import type { ActivityDefinition } from "@/lib/activities";
import { captureClientEvent } from "@/lib/analytics/client";
import { ANALYTICS_EVENTS } from "@/lib/analytics/events";
import { mediaPreviewUrlFromAsset } from "@/lib/media";
import type { ProfileMusicSong } from "@/lib/music/types";
import { PROFILE_GOAL_LABELS } from "@/lib/profile-goals";
import {
  type ProfileBuzzFilter,
  type ProfileTab,
  resolveProfileNavigation,
} from "@/lib/profile-navigation";
import { cn } from "@/lib/utils";
import { ActivityCard } from "./activity-card";
import { BuddiesDrawer } from "./buddies-drawer";
import { PROFILE_DESKTOP } from "./profile-page-desktop";
import { ProfilePostsTab } from "./profile-posts-tab";

function PhotoCard({
  image,
  alt,
  priority = false,
  desktopGrid = false,
}: {
  image: ImageData;
  alt: string;
  priority?: boolean;
  /** One cell of the two-up desktop gallery rather than a centred card. */
  desktopGrid?: boolean;
}) {
  return (
    <div
      className={cn(
        "relative aspect-square w-full overflow-hidden rounded-[32px] border border-border bg-surface shadow-lg wide:mx-auto wide:max-w-lg",
        desktopGrid && PROFILE_DESKTOP.photo,
      )}
    >
      <ProfileMedia
        value={image}
        alt={alt}
        priority={priority}
        videoFit="contain"
        sizes={
          desktopGrid
            ? "(min-width: 1280px) 21rem, (min-width: 1024px) 18rem, (min-width: 640px) 75vw, 92vw"
            : "(min-width: 1024px) 32rem, (min-width: 640px) 75vw, 92vw"
        }
      />
      <div className="absolute inset-0 bg-linear-to-t from-overlay/80 via-overlay/0 to-overlay/10" />
    </div>
  );
}

/**
 * Alt text for one photo in a profile gallery.
 *
 * Content-hashed filenames mean alt text is the entire textual description
 * Google Images has for these files, so the person's name carries it; the
 * position is what stops several photos on one page from competing as
 * duplicates of a single description.
 */
function galleryPhotoAlt(
  fullName: string | null | undefined,
  position: number,
  total: number,
) {
  const name = fullName ?? "Profile";
  return total > 1
    ? `${name} — photo ${position} of ${total}`
    : `${name} photo`;
}

function enrichActivitySkill(
  skill: ActivitySkill,
  activityDictionary: Map<ActivityId, ActivityDefinition>,
) {
  const activity = activityDictionary.get(skill.activityId);

  if (!activity) {
    return null;
  }

  const level = activity.levels.find(
    (entry) => entry.score === skill.skillLevel,
  );

  return {
    activity,
    level,
  };
}

function getOrderedSkillEntries(
  activities: ActivityId[] | null,
  skills: ActivitySkill[] | null,
  activityDictionary: Map<ActivityId, ActivityDefinition>,
) {
  if (!skills?.length) {
    return [];
  }

  const skillsByActivity = new Map(
    skills.map((skill) => [
      skill.activityId,
      enrichActivitySkill(skill, activityDictionary),
    ]),
  );

  const orderedBySelection = (activities ?? []).flatMap((activityId) => {
    const entry = skillsByActivity.get(activityId);

    if (!entry) {
      return [];
    }

    return [{ activityId, ...entry }];
  });

  const remainingEntries = skills
    .filter((skill) => !(activities ?? []).includes(skill.activityId))
    .flatMap((skill) => {
      const entry = enrichActivitySkill(skill, activityDictionary);

      if (!entry) {
        return [];
      }

      return [{ activityId: skill.activityId, ...entry }];
    });

  return [...orderedBySelection, ...remainingEntries];
}

function profileInfoItems(profile: ProfilePageProps["profile"]) {
  return [
    // The username rides with the other facts (a pill on phones, the info
    // row on desktop) instead of a second line under the name.
    profile.handle && { icon: AtSign, text: profile.handle },
    profile.locationDisplayName && {
      icon: MapPin,
      text: profile.locationDisplayName,
    },
    profile.gender && {
      icon: User,
      text: profile.gender.charAt(0).toUpperCase() + profile.gender.slice(1),
    },
    // Date of birth is still collected and still reaches this component on
    // `profile.age` / `profile.dateOfBirth`; it is deliberately not rendered
    // anywhere member-facing.
  ].filter((item): item is { icon: typeof MapPin; text: string } =>
    Boolean(item),
  );
}

export type ProfilePageProps = {
  profile: {
    userId: string;
    fullName: string | null;
    /** The username, shown as `@handle` beside the name. */
    handle?: string | null;
    locationDisplayName: string | null;
    gender: string | null;
    dateOfBirth?: string | null;
    age?: number | null;
    activities: ActivityId[] | null;
    activitySkills: ActivitySkill[] | null;
    images: ImageData[] | null;
    bio: string | null;
    goals: Goal[] | null;
    isComplete: boolean;
    hiives?: { id: number; name: string; slug: string; logo: string }[];
    events?: {
      id: number;
      name: string;
      slug: string;
      banner: string;
      hiiveSlug: string;
    }[];
    friendCount?: number;
    mutualFriendCount?: number;
    /** Songs on the profile ("On repeat"); absent or empty = no music. */
    music?: ProfileMusicSong[];
  };
  slots?: {
    headerActions?: React.ReactNode;
  };
  activities?: ReadonlyArray<ActivityDefinition>;
  viewer?: CommentsSheetViewer;
  /** Story ring status for this profile's user stories (server-resolved). */
  storyRingStatus?: EntityStoryStatus | null;
  /** Hide the fixed header - useful when rendering inside a drawer or modal */
  hideHeader?: boolean;
  /** When provided, renders a back button in the header. Uses `?from=` query param if present, else this fallback. */
  backFallbackHref?: string;
};

type ProfileDetailsTabContentProps = {
  profile: ProfilePageProps["profile"];
  activities: ReadonlyArray<ActivityDefinition>;
  onOpenBuddies: () => void;
  /**
   * Page mode on a desktop-capable layout: from `lg` the info pills and About
   * move to the side column, and the rest lays out as a two-column grid.
   */
  desktopSplit: boolean;
  /** Owners get "Add music" / Edit; visitors never see an empty state. */
  isOwnProfile: boolean;
};

function ProfileDetailsTabContent({
  profile,
  activities,
  onOpenBuddies,
  desktopSplit,
  isOwnProfile,
}: ProfileDetailsTabContentProps) {
  // Every class below is `wide:`-only, and only in page mode, so the phone
  // column and the drawer render exactly as before.
  const d = (className: string) => (desktopSplit ? className : undefined);
  // Back from a hiive opened on this rail should land on the same tile.
  const hiivesRailRef = useRailScrollRestore<HTMLDivElement>(
    `profile-hiives:${profile.userId}`,
  );

  const activityDictionary = new Map(
    activities.map((activity) => [activity.id, activity] as const),
  );
  const activitySkills = getOrderedSkillEntries(
    profile.activities ?? null,
    profile.activitySkills ?? null,
    activityDictionary,
  );
  const sortedImages = [...(profile.images ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const galleryImages = sortedImages.slice(1);

  const infoItems = profileInfoItems(profile);
  // Optional, owner-only: the way in to music while there's none yet.
  const showAddMusic = isOwnProfile && (profile.music ?? []).length === 0;

  const [visibleActivityCount, setVisibleActivityCount] = useState(5);

  const handleLoadMoreActivities = () => {
    if (visibleActivityCount >= activitySkills.length) {
      setVisibleActivityCount(5); // Reset loop
    } else {
      setVisibleActivityCount((prev) => prev + 5);
    }
  };

  return (
    <div className={cn("flex flex-col gap-6 p-3", d(PROFILE_DESKTOP.details))}>
      {sortedImages.length === 0 && (
        <div
          className={cn(
            "rounded-[24px] border border-border bg-surface px-3 py-5 shadow-sm backdrop-blur-md dark:border-glass-border-10 dark:bg-glass-bg-05",
            d(PROFILE_DESKTOP.section),
          )}
        >
          <h3 className="mb-3 text-sm font-semibold tracking-wider text-text-secondary uppercase">
            Gallery
          </h3>
          <div className="flex flex-col gap-2">
            <h3 className="text-xl font-semibold text-text-primary dark:text-white">
              Add your first photo
            </h3>
            <p className="text-sm leading-relaxed text-text-secondary">
              Profiles with photos get more responses. Upload a few shots to let
              people see who you are.
            </p>
          </div>
        </div>
      )}

      {(infoItems.length > 0 || profile.friendCount !== undefined) && (
        <div
          className={cn(
            "scrollbar-hide flex gap-2 overflow-x-auto py-1",
            d("wide:hidden"),
          )}
        >
          {profile.friendCount !== undefined && profile.friendCount > 0 && (
            <button
              type="button"
              className="group hover:bg-surface-hover flex min-w-fit items-center gap-2 rounded-full border border-border bg-surface-alt px-4 py-2 shadow-sm backdrop-blur-md transition-all active:scale-95 dark:border-glass-border-10 dark:bg-glass-bg-10 dark:hover:bg-white/20"
              onClick={onOpenBuddies}
            >
              <Users className="h-3.5 w-3.5 shrink-0 text-text-secondary transition-transform group-hover:scale-110 dark:text-white" />
              <span className="text-xs font-semibold text-text-secondary dark:text-white">
                {profile.friendCount} Buddies
              </span>
            </button>
          )}
          {infoItems.map((item) => {
            if (!item) return null;
            const Icon = item.icon;
            return (
              <div
                key={item.text}
                className="flex min-w-fit items-center gap-2 rounded-full border border-border bg-surface-alt px-4 py-2 shadow-sm backdrop-blur-md dark:border-glass-border-10 dark:bg-glass-bg-10"
              >
                <Icon className="h-3.5 w-3.5 shrink-0 text-text-secondary dark:text-white" />
                <span className="text-xs font-medium text-text-secondary dark:text-white">
                  {item.text}
                </span>
              </div>
            );
          })}
        </div>
      )}

      <StoryHighlightsRow
        ownerType="user"
        ownerId={profile.userId}
        className={d(PROFILE_DESKTOP.section)}
      />

      <div
        className={cn("flex flex-col space-y-3", d(PROFILE_DESKTOP.section))}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
            {activitySkills.length} Activities
          </h3>
        </div>
        <div>
          {activitySkills.length === 0 ? (
            <p className="text-sm leading-relaxed text-text-secondary dark:text-white">
              No activities have been set yet.
            </p>
          ) : (
            <div
              className={cn(
                "flex flex-col space-y-2",
                d(PROFILE_DESKTOP.twoUpList),
              )}
            >
              {activitySkills.slice(0, visibleActivityCount).map((entry) => {
                const { activity, level, activityId } = entry;

                if (!activity) {
                  return null;
                }

                return (
                  <ActivityCard
                    key={activityId}
                    activitySkill={{
                      activityId,
                      activity,
                      level: level || null,
                    }}
                    variant="compact"
                    className="border-border bg-surface-alt text-text-primary shadow-sm backdrop-blur-md dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white"
                    onSelect={() =>
                      captureClientEvent(
                        ANALYTICS_EVENTS.PROFILE_ACTIVITY_CLICKED,
                        {
                          activity_id: activityId,
                          activity_name: activity.name,
                          skill_level: level?.score,
                        },
                      )
                    }
                  />
                );
              })}
              {activitySkills.length > 5 && (
                <button
                  type="button"
                  onClick={handleLoadMoreActivities}
                  className={cn(
                    "hover:bg-surface-hover w-full rounded-2xl border border-border bg-surface-alt py-3 text-center text-xs font-semibold text-text-primary shadow-sm backdrop-blur-md transition-colors active:scale-[0.99] dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white dark:hover:bg-glass-bg-10",
                    d(PROFILE_DESKTOP.section),
                  )}
                >
                  {visibleActivityCount >= activitySkills.length
                    ? "Show Top 5"
                    : `Load +${Math.min(5, activitySkills.length - visibleActivityCount)} More`}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <div
        className={cn("flex flex-col space-y-3", d(PROFILE_DESKTOP.section))}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
            Member of
          </h3>
        </div>
        {!profile.hiives || profile.hiives.length === 0 ? (
          <p className="text-base leading-relaxed text-text-secondary dark:text-white">
            Not a member of any Hiives yet.
          </p>
        ) : (
          <div
            ref={hiivesRailRef}
            className="scrollbar-hide flex gap-3 overflow-x-auto pb-2"
          >
            {profile.hiives.map((hiive) => (
              <BackAwareLink
                key={hiive.id}
                href={`/hiives/${hiive.slug}`}
                className="group flex w-14 flex-col items-center gap-1.5"
              >
                <div className="relative size-14 overflow-hidden rounded-xl border border-border bg-surface-alt shadow-sm backdrop-blur-md transition-transform group-hover:scale-105 group-active:scale-95 dark:border-glass-border-10 dark:bg-glass-bg-05">
                  <Image
                    src={hiive.logo}
                    alt={hiive.name}
                    fill
                    className="object-cover"
                  />
                </div>
                <span className="w-full truncate text-center text-[10px] font-medium text-text-secondary dark:text-white">
                  {hiive.name}
                </span>
              </BackAwareLink>
            ))}
          </div>
        )}
      </div>

      <div
        className={cn("flex flex-col space-y-3", d(PROFILE_DESKTOP.section))}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
            Recent Events
          </h3>
        </div>
        {!profile.events || profile.events.length === 0 ? (
          <p className="text-base leading-relaxed text-text-secondary dark:text-white">
            No recent events attended.
          </p>
        ) : (
          <div
            className={cn(
              "flex max-h-96 flex-col gap-2 overflow-y-auto pb-2",
              d("wide:grid wide:content-start wide:@lg:grid-cols-2"),
            )}
          >
            {profile.events.map((event) => (
              <BackAwareLink
                key={event.id}
                href={`/hiives/${event.hiiveSlug}/events/${event.slug}`}
                className="group hover:bg-surface-hover flex items-center gap-3 rounded-xl border border-border bg-surface-alt p-2 shadow-sm backdrop-blur-md transition-colors active:scale-[0.99] dark:border-glass-border-10 dark:bg-glass-bg-05 dark:hover:bg-glass-bg-10"
              >
                <div className="relative h-10 w-10 shrink-0 overflow-hidden rounded-lg bg-surface-alt">
                  <Image
                    src={event.banner}
                    alt={event.name}
                    fill
                    className="object-cover"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h5 className="truncate text-sm font-medium text-text-primary dark:text-white">
                    {event.name}
                  </h5>
                  <p className="text-[10px] text-text-tertiary">View event</p>
                </div>
              </BackAwareLink>
            ))}
          </div>
        )}
      </div>

      {galleryImages[0] && (
        // Numbered, because every photo on the page shared one alt string —
        // identical text across a gallery is a single description split across
        // several images, and a screen reader reads "photo" over and over.
        <PhotoCard
          image={galleryImages[0]}
          alt={galleryPhotoAlt(profile.fullName, 1, galleryImages.length)}
          desktopGrid={desktopSplit}
        />
      )}

      {/* On desktop the bio lives in the side column (ProfileSummary). */}
      <div className={cn("flex flex-col space-y-3", d("wide:hidden"))}>
        {/* Always h-8 — the Add music button's height — so the row is the
            same with or without it, and matches the skeleton. */}
        <div className="flex h-8 items-center justify-between gap-3">
          <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
            About me
          </h3>
          {showAddMusic ? <AddMusicLiquidButton /> : null}
        </div>
        <div className="text-sm leading-relaxed text-text-secondary dark:text-white">
          {profile.bio ? (
            <>
              <p className="wrap-break-word whitespace-pre-wrap text-text-secondary dark:text-white">
                {profile.bio}
              </p>
              {(profile.goals ?? []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {profile.goals?.map((goal) => (
                    <Badge
                      key={goal}
                      variant="outline"
                      className="border-border bg-surface-alt px-2.5 py-1 text-xs font-medium text-text-secondary capitalize shadow-sm backdrop-blur-md dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white"
                      onClick={() =>
                        captureClientEvent(
                          ANALYTICS_EVENTS.PROFILE_GOAL_BADGE_CLICKED,
                          {
                            goal: goal,
                          },
                        )
                      }
                    >
                      {PROFILE_GOAL_LABELS[goal] ?? goal}
                    </Badge>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-text-primary">
              Tell others about yourself by updating your bio.
            </p>
          )}
        </div>
      </div>

      {/* Music sits where the next photo would: right under About me. */}
      <ProfileMusicSection
        songs={profile.music ?? []}
        ownerUserId={profile.userId}
        ownerName={profile.fullName}
        isOwnProfile={isOwnProfile}
        className={d(PROFILE_DESKTOP.section)}
      />

      {galleryImages.slice(1).map((image, index) => (
        <PhotoCard
          key={`photo-${image.id}`}
          image={image}
          alt={galleryPhotoAlt(
            profile.fullName,
            index + 2,
            galleryImages.length,
          )}
          desktopGrid={desktopSplit}
        />
      ))}

      {/* Last on the page: stickers are a flourish, not who someone is. */}
      <ProfileStickersSection
        userId={profile.userId}
        ownerName={profile.fullName}
        isOwnProfile={isOwnProfile}
        className={d(PROFILE_DESKTOP.section)}
      />
    </div>
  );
}

/**
 * The desktop side column's "who is this" block, under the hero: name, the
 * Instagram-style counts row, location/gender, then the bio and goals. On a
 * phone this content lives in the Details tab instead (pills + About me), so
 * this only ever renders from `lg`.
 */
function ProfileSummary({
  profile,
  activityCount,
  onOpenBuddies,
  isOwnProfile,
}: {
  profile: ProfilePageProps["profile"];
  activityCount: number;
  onOpenBuddies: () => void;
  isOwnProfile: boolean;
}) {
  const bioRef = useRef<HTMLParagraphElement>(null);
  const [bioExpanded, setBioExpanded] = useState(false);
  const bioIsClamped = useIsClampedOverflow(bioRef, profile.bio ?? "");
  const infoItems = profileInfoItems(profile);
  const hiiveCount = profile.hiives?.length;
  const buddyCount = profile.friendCount;

  return (
    <div className="hidden flex-col gap-4 wide:flex">
      <div className="flex flex-col gap-3">
        <p className="text-2xl leading-tight font-semibold tracking-tight wrap-break-word text-text-primary">
          {profile.fullName ?? "Your Profile"}
        </p>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-text-secondary">
          <span>
            <span className="font-semibold text-text-primary">
              {activityCount}
            </span>{" "}
            {activityCount === 1 ? "activity" : "activities"}
          </span>
          {hiiveCount !== undefined ? (
            <span>
              <span className="font-semibold text-text-primary">
                {hiiveCount}
              </span>{" "}
              {hiiveCount === 1 ? "hiive" : "hiives"}
            </span>
          ) : null}
          {buddyCount !== undefined ? (
            // Opens the list, like tapping "followers" — a zero count has
            // nothing to open.
            <button
              type="button"
              disabled={buddyCount === 0}
              onClick={onOpenBuddies}
              className="rounded-md text-left transition-colors duration-150 ease-out-strong disabled:cursor-default enabled:pointer-fine:hover:text-text-primary"
            >
              <span className="font-semibold text-text-primary">
                {buddyCount}
              </span>{" "}
              {buddyCount === 1 ? "buddy" : "buddies"}
            </button>
          ) : null}
        </div>
        {infoItems.length > 0 ? (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-text-secondary">
            {infoItems.map((item) => {
              const Icon = item.icon;
              return (
                <span key={item.text} className="flex items-center gap-1.5">
                  <Icon aria-hidden className="size-3.5 shrink-0" />
                  {item.text}
                </span>
              );
            })}
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 border-t border-border pt-4 dark:border-glass-border-10">
        {isOwnProfile && (profile.music ?? []).length === 0 ? (
          <div className="flex min-h-8 items-center justify-between gap-3">
            <h3 className="text-xs font-bold tracking-wider text-text-tertiary uppercase">
              About me
            </h3>
            <AddMusicLiquidButton />
          </div>
        ) : null}
        {profile.bio ? (
          <div className="text-sm leading-relaxed text-text-secondary">
            <p
              ref={bioRef}
              className={cn(
                "wrap-break-word whitespace-pre-wrap",
                !bioExpanded && "line-clamp-4",
              )}
            >
              {profile.bio}
            </p>
            {bioIsClamped || bioExpanded ? (
              <button
                type="button"
                onClick={() => setBioExpanded((expanded) => !expanded)}
                className="mt-1 text-xs font-semibold text-text-primary pointer-fine:hover:underline"
              >
                {bioExpanded ? "Show less" : "More"}
              </button>
            ) : null}
          </div>
        ) : (
          <p className="text-sm text-text-primary">
            Tell others about yourself by updating your bio.
          </p>
        )}
        {(profile.goals ?? []).length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {profile.goals?.map((goal) => (
              <Badge
                key={goal}
                variant="outline"
                className="border-border bg-surface-alt px-2.5 py-1 text-xs font-medium text-text-secondary capitalize dark:border-glass-border-10 dark:bg-glass-bg-05 dark:text-white"
              >
                {PROFILE_GOAL_LABELS[goal] ?? goal}
              </Badge>
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

export function ProfilePageView({
  profile,
  slots,
  activities = [],
  viewer,
  storyRingStatus,
  hideHeader = false,
  backFallbackHref,
}: ProfilePageProps) {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const sortedImages = [...(profile.images ?? [])].sort(
    (a, b) => a.order - b.order,
  );
  const heroImage = sortedImages[0];
  const [buddiesOpen, setBuddiesOpen] = useState(false);
  const openBuddies = () => setBuddiesOpen(true);

  // Page mode is the only place the two-column desktop frame applies; inside
  // a drawer the profile stays one column at every width.
  const pageMode = !hideHeader;
  const desktop = (className: string) => (pageMode ? className : undefined);
  const activityCount = getOrderedSkillEntries(
    profile.activities ?? null,
    profile.activitySkills ?? null,
    new Map(activities.map((activity) => [activity.id, activity] as const)),
  ).length;

  const showTabs = !hideHeader;
  const query = searchParams.toString();
  const navigation = resolveProfileNavigation(query);
  const activeTab = showTabs ? navigation.tab : "details";
  const canonicalQuery = navigation.query;

  useEffect(() => {
    if (!showTabs || canonicalQuery === query) return;
    const href = canonicalQuery ? `${pathname}?${canonicalQuery}` : pathname;
    window.history.replaceState(null, "", `${href}${window.location.hash}`);
  }, [showTabs, canonicalQuery, query, pathname]);

  // Buddy-funnel entry: view -> send request -> accept. Only other-user
  // views feed the funnel — own-profile views would inflate the denominator.
  const isOwnProfile = pathname === "/profile" || viewer?.id === profile.userId;
  useScreenView(
    ANALYTICS_EVENTS.USER_PROFILE_VIEWED,
    isOwnProfile ? null : profile.userId,
    {
      viewed_user_id: profile.userId,
      viewer_is_authenticated: Boolean(viewer?.id),
    },
  );

  const updateNavigation = (
    tab: ProfileTab,
    filter: ProfileBuzzFilter = "all",
  ) => {
    if (!showTabs) return;
    const params = new URLSearchParams(query);
    params.set("tab", tab);
    params.set("filter", filter);
    const nextQuery = resolveProfileNavigation(params.toString()).query;
    if (nextQuery === query) return;
    const href = nextQuery ? `${pathname}?${nextQuery}` : pathname;
    window.history.pushState(null, "", `${href}${window.location.hash}`);
  };

  const handleTabChange = (value: string) => {
    updateNavigation(value === "buzz" ? "buzz" : "details");
  };

  return (
    <div className="bg-background pb-4">
      {!hideHeader && (
        <>
          <header
            className={cn(
              "fixed top-0 right-0 left-0 z-50 border-b border-border/50 bg-background/80 pt-[env(safe-area-inset-top)] backdrop-blur-md supports-backdrop-filter:bg-background/60",
              PROFILE_DESKTOP.barColumn,
            )}
          >
            <div
              className={cn(
                "mx-auto flex h-14 max-w-3xl items-center justify-between gap-4 px-4",
                PROFILE_DESKTOP.barInner,
                PROFILE_DESKTOP.barColumnRow,
              )}
            >
              <div className="flex min-w-0 flex-1 items-center gap-2">
                {backFallbackHref ? (
                  <BackButton fallbackHref={backFallbackHref} />
                ) : null}
                <h1
                  className={cn(
                    "truncate text-lg font-semibold text-text-primary",
                    PROFILE_DESKTOP.barTitle,
                  )}
                >
                  {profile.fullName ?? "Your Profile"}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                {slots?.headerActions}
              </div>
            </div>
          </header>

          <div className={cn("h-14", PROFILE_DESKTOP.barSpacer)} />
        </>
      )}

      <div
        className={cn(
          "mx-auto w-full max-w-3xl",
          desktop(PROFILE_DESKTOP.grid),
        )}
      >
        <aside
          aria-label="Profile summary"
          className={desktop(PROFILE_DESKTOP.side)}
        >
          {/* Always square: profile photos are cropped square at upload, so a
              size cap narrows the photo and never crops it. Never taller than
              56% of the screen, so on a wide portrait screen (iPhone Duo open
              and rotated, 669pt) the name and tabs still show; from `sm` it
              is then a centred rounded card rather than a full-bleed hero. */}
          <div
            className={cn(
              "relative isolate mx-auto aspect-square w-full max-w-[56dvh] overflow-hidden bg-surface sm:mt-4 sm:rounded-3xl wide:mt-0 wide:max-w-none",
              pageMode
                ? PROFILE_DESKTOP.hero
                : "wide:mx-auto wide:mt-4 wide:max-w-lg wide:rounded-[32px]",
            )}
          >
            {heroImage ? (
              <ProfileMedia
                value={heroImage}
                alt={`${profile.fullName ?? "Profile"} cover photo`}
                priority
                videoFit="contain"
                sizes={
                  pageMode
                    ? "(min-width: 1280px) 23rem, (min-width: 1024px) 20rem, (min-width: 768px) 48rem, 100vw"
                    : "(min-width: 768px) 32rem, 90vw"
                }
              />
            ) : (
              <div className="absolute inset-0 bg-linear-to-br from-brand/10 via-accent/10 to-surface" />
            )}
            <div className="absolute inset-0 bg-linear-to-t from-overlay/60 via-transparent to-overlay/40" />
            {storyRingStatus?.hasActive ? (
              <div className="absolute bottom-3 left-3 z-10">
                <EntityStoryRing
                  authorType="user"
                  authorId={profile.userId}
                  name={profile.fullName ?? "Profile"}
                  avatarUrl={mediaPreviewUrlFromAsset(heroImage) ?? null}
                  href={null}
                  status={storyRingStatus}
                  size={72}
                  shape="squircle"
                >
                  <Avatar className="h-full w-full rounded-none">
                    <AvatarImage
                      src={mediaPreviewUrlFromAsset(heroImage) ?? undefined}
                    />
                    <AvatarFallback>
                      {(profile.fullName ?? "?").charAt(0).toUpperCase()}
                    </AvatarFallback>
                  </Avatar>
                </EntityStoryRing>
              </div>
            ) : null}
          </div>

          {pageMode ? (
            <ProfileSummary
              profile={profile}
              activityCount={activityCount}
              onOpenBuddies={openBuddies}
              isOwnProfile={isOwnProfile}
            />
          ) : null}
        </aside>

        <div className={desktop(PROFILE_DESKTOP.main)}>
          {showTabs && (
            <div
              className={cn(
                "sticky top-[calc(env(safe-area-inset-top)+3.5rem)] z-40 bg-gradient-to-b from-background/95 via-background/82 to-transparent px-2 py-2 backdrop-blur-md transition-opacity",
                PROFILE_DESKTOP.tabsBar,
              )}
            >
              <div className="mx-auto w-full max-w-3xl">
                <Tabs value={activeTab} onValueChange={handleTabChange}>
                  <TabsList className="flex w-full">
                    <TabsTrigger value="details" className="flex-1">
                      Details
                    </TabsTrigger>
                    <TabsTrigger value="buzz" className="flex-1">
                      Buzz
                    </TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
            </div>
          )}

          <div className="mx-auto w-full max-w-3xl">
            {activeTab === "details" ? (
              <ProfileDetailsTabContent
                profile={profile}
                activities={activities}
                onOpenBuddies={openBuddies}
                desktopSplit={pageMode}
                isOwnProfile={isOwnProfile}
              />
            ) : (
              <ProfilePostsTab
                userId={profile.userId}
                viewer={viewer}
                view={navigation.filter}
                onViewChange={(filter) => updateNavigation("buzz", filter)}
              />
            )}
          </div>
        </div>
      </div>

      <BuddiesDrawer
        open={buddiesOpen}
        onOpenChange={setBuddiesOpen}
        userId={profile.userId}
      />
    </div>
  );
}
